import React, { useEffect, useMemo } from 'react';
import { Tooltip, AreaChart, ResponsiveContainer, CartesianGrid, XAxis, Area } from 'recharts';

import { Card, CardContent, Grid, Typography } from '@material-ui/core';

import { makeStyles, Theme } from '@material-ui/core/styles';

import { COLOR_E, COLOR_TOOLTIP_BACKGROUND, GRAPH_EXPORT_LIMIT_MULTIPLIER } from 'src/utils/const';
import { AreaChartRatioCardPropTypes } from 'src/interfaces/iproptypes';
import { AreaChartGraphObject, ParsedAreaChartValues } from 'src/interfaces/idashboard';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import { Box, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { infoIconButton, infoIconStyle } from 'src/App/Dashboards/Market/styles/style';
import { COLOR_GREY_600, COLOR_PRIMARY_1_700, COLOR_PRIMARY_2, GRAPH_BLUE } from 'src/utils/colors';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import { logAmplitudeEvent } from 'src/utils/analytics';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { getGraphExportCreditRequired } from 'src/utils/functions';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flex: 1,
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
    [theme.breakpoints.up('sm')]: {
      maxHeight: 300,
    },
  },
  successText: {
    color: theme.palette.success.main,
    fontWeight: 900,
  },
  errorText: {
    color: theme.palette.error.main,
    fontWeight: 900,
  },
  subtleText: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  rateText: {
    fontWeight: 900,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 100,
  },
  sampleText: {
    color: COLOR_E,
    fontWeight: 600,
    transform: 'rotate(-30deg)',
    msTransform: 'rotate(-30deg)' /* IE 9 */,
    webkitTransform: 'rotate(-30deg),' /* Opera, Chrome, and Safari */,
  },
  ledgendColorBox: {
    width: '20px',
    height: '10px',
    borderRadius: '1px',
  },
  ledgendContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
  },
}));

const months = {
  0: 'Jan',
  1: 'Feb',
  2: 'Mar',
  3: 'Apr',
  4: 'May',
  5: 'Jun',
  6: 'Jul',
  7: 'Aug',
  8: 'Sept',
  9: 'Oct',
  10: 'Nov',
  11: 'Dec',
};

const isEmpty = (obj: string): boolean => {
  return !(Object.keys(obj).length > 0);
};

const sortGuestArrayByDate = (guestArray: AreaChartGraphObject[]): AreaChartGraphObject[] => {
  if (!guestArray || !Array.isArray(guestArray)) return guestArray;

  return guestArray.sort((a, b) => {
    const dateA = new Date(a.name);
    const dateB = new Date(b.name);

    return dateA.getTime() - dateB.getTime();
  });
};

const parseGraphVal = (
  values: ParsedAreaChartValues,
  setSelectedGraphExportDetails: any
): [AreaChartGraphObject[], string[][]] => {
  if (!values) {
    return [
      [
        {
          name: '2020-01-01',
          domestic: 0,
          international: 0,
        },
      ],
      [],
    ];
  }
  let valuesObj: ParsedAreaChartValues = values;
  let graphObj: AreaChartGraphObject[] = [];
  let domestic = valuesObj['domestic'];
  let international = valuesObj['international'];
  let common_obj = Object.keys(domestic).length !== 0 ? domestic : international;
  for (var date in common_obj) {
    let objToInsert = {
      name: date,
      domestic: domestic ? domestic[date] : 0,
      international: international ? international[date] : 0,
    };
    graphObj.push(objToInsert);
  }
  graphObj = sortGuestArrayByDate(graphObj);

  let exportableGraphData =
    graphObj.length > 0
      ? graphObj
          .map(item => [item.name, item.domestic + '%', item.international + '%'])
          .sort((a, b) => a[0].localeCompare(b[0]))
          .reverse()
      : [];
  exportableGraphData.length > 0 &&
    exportableGraphData.unshift(['Date', 'Domestic Guests', 'International Guests']);

  return [graphObj, exportableGraphData];
};
/*
{2018: {…}, 2019: {…}, 2020: {…}}
[{"name": "1", "2018":22, "2019":33}, ]
2018: {1: 24.9, 2: 27.2, 3: 33.5, 4: 42.1, 5: 49.1, 6: 55.2, 7: 53.2, 8: 52.7, 9: 50.2, 10: 48.6, 11: 43.3, 12: 34.2, total: 207}
2019: {1: 30.2, 2: 33.2, 3: 38.5, 4: 45.7, 5: 51.7, 6: 57.8, 7: 55.9, 8: 50.5, 9: 45.5, 10: 42.5, 11: 38.8, 12: 38.5, total: 319}
2020: {1: 41.5, 2: 40.9, 3: 27.9, 4: 13.6, 5: 3.5, total: 348}
*/

const animationTime = 120;

const CustomTooltip = ({ active, payload, label, classes, parent }) => {
  useEffect(() => {
    if (active && parent) {
      logAmplitudeEvent('tooltip_interacted', {
        tooltip_type: 'graph',
        parent: parent,
      });
    }
  }, [active]);

  if (active) {
    return (
      <Card style={{ boxShadow: `0px 0px 10px 0px ${COLOR_GREY_600}` }}>
        <CardContent style={{ background: '#fff' }}>
          <Typography variant="body1" style={{ fontWeight: 'bolder', color: COLOR_GREY_600 }}>
            {payload[0].payload.name}
          </Typography>
          <Typography variant="body2" style={{ fontWeight: 500, color: COLOR_GREY_600 }}>
            Domestic:{' '}
            <span style={{ color: GRAPH_BLUE }}>
              {typeof payload[0].payload.domestic === 'undefined'
                ? 'N/A'
                : `${payload[0].payload.domestic}`}
              % of total guests
            </span>
            <br />
            International:{' '}
            <span style={{ color: COLOR_PRIMARY_1_700 }}>
              {typeof payload[0].payload.international === 'undefined'
                ? 'N/A'
                : `${payload[0].payload.international}`}
              % of total guests
            </span>
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return null;
};

const AreaChartRatioCard = ({
  title,
  data,
  tooltipContent,
  isDisabled,
  handleOnSampleClick,
  userTailorMarketAccess,
  parent,
  warningTextComponent,
  graphRef,
  setSelectedGraphExportDetails,
}: AreaChartRatioCardPropTypes) => {
  const classes = useStyles();

  const upgradeMessage = useUpgradeMesageProFeature();

  const [graphData, exportableGraphData] = useMemo(() => {
    return parseGraphVal(data, setSelectedGraphExportDetails);
  }, [data]);

  let noPropertyAvail: boolean = false;
  if (graphData.length === 0) {
    noPropertyAvail = true;
  }

  let blurGraph = {};

  return (
    <Card className={classes.root}>
      <CardContent>
        {warningTextComponent && warningTextComponent}
        <Grid container>
          <Grid container item xs={12} justifyContent="space-between" alignItems="center">
            <Typography variant="body1" className="subHeadings">
              {title}
              {tooltipContent && <InfoButton title={title} tooltipContent={tooltipContent} />}
            </Typography>

            <IconButton
              color="primary"
              onClick={() =>
                exportableGraphData.length &&
                setSelectedGraphExportDetails({
                  graphData: exportableGraphData,
                  fieldName: 'guest_origin_graph_domestic_vs_international.csv',
                  graphType: 'guestOriginGraphs',
                })
              }
            >
              <DownloadRoundedIcon />
            </IconButton>
          </Grid>

          <Grid item container xs={12} justifyContent="flex-end">
            <Grid
              container
              item
              xs={12}
              style={{ position: 'relative', top: '0', left: '0' }}
              ref={graphRef}
            >
              <ResponsiveContainer height={180} width="100%">
                {noPropertyAvail ? (
                  <Typography color="textSecondary">
                    Not enough data matching this criteria.{' '}
                  </Typography>
                ) : (
                  <AreaChart
                    data={graphData}
                    margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
                    style={blurGraph}
                    sx={{ pointerEvents: isDisabled ? 'none' : 'auto' }}
                  >
                    <defs>
                      <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={GRAPH_BLUE} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={GRAPH_BLUE} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLOR_PRIMARY_1_700} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLOR_PRIMARY_1_700} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="name" hide={true} />

                    <CartesianGrid strokeDasharray="3 3" />
                    <Tooltip
                      content={
                        <CustomTooltip classes={classes} active payload label parent={parent} />
                      }
                      position={{ y: -70 }}
                      allowEscapeViewBox={{ x: false, y: true }}
                    />

                    <Area
                      type="monotone"
                      dataKey="domestic"
                      stroke={GRAPH_BLUE}
                      fillOpacity={1}
                      fill="url(#colorUv)"
                      isAnimationActive={true}
                      animationDuration={animationTime}
                    />
                    <Area
                      type="monotone"
                      dataKey="international"
                      stroke={COLOR_PRIMARY_1_700}
                      fillOpacity={1}
                      fill="url(#colorPv)"
                      isAnimationActive={true}
                      animationDuration={animationTime}
                    />
                  </AreaChart>
                )}
              </ResponsiveContainer>

              {isDisabled && (
                <div
                  style={{
                    height: '100%',
                    position: 'absolute',
                    top: '0',
                    left: '0px',
                    width: '100%',
                    background: '#ffffff',
                    filter: 'blur( 6px)',
                    opacity: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                ></div>
              )}
              {isDisabled && (
                <Typography
                  style={{
                    position: 'absolute',
                    top: '40%',
                    left: '50%',
                    fontSize: '20px',
                    fontWeight: 500,
                    color: COLOR_PRIMARY_2,
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    textDecoration: 'underline',
                    transform: 'translate(-50%, -50%)',
                  }}
                  onClick={() => {
                    handleOnSampleClick('expert');
                  }}
                >
                  {upgradeMessage}
                </Typography>
              )}
            </Grid>

            <Grid
              item
              container
              xs={12}
              justifyContent="center"
              alignItems="center"
              spacing={4}
              style={{ marginTop: '2px', color: COLOR_GREY_600 }}
            >
              <Grid item className={classes.ledgendContainer}>
                <Box className={classes.ledgendColorBox} sx={{ background: GRAPH_BLUE }} />{' '}
                <Typography variant="body2">Domestic</Typography>
              </Grid>
              <Grid item className={classes.ledgendContainer}>
                <Box className={classes.ledgendColorBox} sx={{ background: COLOR_PRIMARY_1_700 }} />{' '}
                <Typography variant="body2">International</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AreaChartRatioCard;
